import { useMemo } from 'react';
import { Heading, Popover, PopoverContent, PopoverTrigger, Stack } from '@customerio/pluma-components/react';
import type { EnvironmentsSchemaSegment } from '@customerio/hydra-ui-api';
import { ConditionNodeRenderer, parseConditions, type ConditionNode } from '@/features/condition-viewer';
import { formatNumberAndPluralize } from '@/utils/number';
import { readAttributeCondition, type AttributeConditionReading } from '../utils/segment-conditions';
import { AttributeCondition } from './attribute-condition';

/**
 * Past this many, a segment's conditions are counted rather than drawn, and
 * the count opens onto them: a row stays one or three conditions tall
 * whatever the segment does (`data-index-item/segments/row/index.gts`).
 */
const MAX_CONDITIONS_SHOWN = 3;

/**
 * One condition as the list draws it: an attribute condition in the chips of
 * Ember's older viewer, which is what this column is in Ember, and anything
 * else in the shared viewer.
 */
type ListedCondition =
  | { kind: 'attribute'; condition: AttributeConditionReading }
  | { kind: 'other'; node: ConditionNode };

function listCondition(raw: unknown): ListedCondition {
  const condition = readAttributeCondition(raw);
  return condition == null ? { kind: 'other', node: parseConditions(raw) } : { kind: 'attribute', condition };
}

type ConditionListProps = {
  conditions: readonly ListedCondition[];
  workspaceId: number;
};

function ConditionList({ conditions, workspaceId }: ConditionListProps) {
  return (
    <Stack gap="200">
      {conditions.map((listed, index) =>
        // Keyed by position: stored conditions carry no id, and their order is
        // the segment's own.
        listed.kind === 'attribute' ? (
          <AttributeCondition key={String(index)} condition={listed.condition} />
        ) : (
          <ConditionNodeRenderer key={String(index)} node={listed.node} workspaceId={workspaceId} />
        ),
      )}
    </Stack>
  );
}

type SegmentConditionsProps = {
  /**
   * The segment's conditions that read the item, and only those: the detail
   * read sends each segment with the conditions that made it count as usage.
   */
  conditions: EnvironmentsSchemaSegment['conditions'];
  workspaceId: number;
};

/** How a segment uses an attribute: the conditions of its that read it. */
export function SegmentConditions({ conditions, workspaceId }: SegmentConditionsProps) {
  const parsed = useMemo(() => (conditions ?? []).map(listCondition), [conditions]);

  if (parsed.length <= MAX_CONDITIONS_SHOWN) {
    return <ConditionList conditions={parsed} workspaceId={workspaceId} />;
  }

  return (
    <Popover shouldTriggerOnClick={false} shouldTriggerOnHover shouldTriggerOnFocus>
      <PopoverTrigger cursor="help">Used in {formatNumberAndPluralize(parsed.length, 'condition')}</PopoverTrigger>
      <PopoverContent>
        <Stack gap="100">
          <Heading level="3">Segment conditions</Heading>
          <ConditionList conditions={parsed} workspaceId={workspaceId} />
        </Stack>
      </PopoverContent>
    </Popover>
  );
}
